import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react'
import { companyService } from '@/services/company.service'
import { brandingService } from '@/services/branding.service'
import { tokenStorage } from '@/lib/api'
import { applyBrandingCssVariables, removeBrandingCssVariables } from '@/hooks/useBranding'
import type { Company } from '@/types/company.types'

import type { UserRole } from '@/types/company.types'

export interface ImpersonatedWhitelabel {
  id: string
  name: string
  slug?: string
  branding?: {
    primary_color?: string
    primary_hover?: string
    primary_light?: string
    brand_name?: string | null
  }
}

export interface CompanyContextValue {
  companies: Company[]
  activeCompany: Company | null
  loadingCompanies: boolean
  isGlobalMaster: boolean
  isTenantMaster: boolean
  isMasterCompany: boolean
  isPersonalAccount: boolean
  isOwnerAccount: boolean
  isTitularTenant: boolean
  isChildCompany: boolean
  userRole: UserRole | null
  impersonatedWhitelabel: ImpersonatedWhitelabel | null
  switchCompany: (company: Company) => Promise<void> | void
  switchToPersonalAccount: () => Promise<Company | null>
  switchToBusinessAccount: () => Promise<Company | null>
  refreshCompanies: () => Promise<void>
  enterWhitelabelEnvironment: (
    whitelabelId: string,
    whitelabelName: string,
    branding?: { primary_color?: string; primary_hover?: string; primary_light?: string; brand_name?: string | null },
    scopedToken?: string
  ) => Promise<void>
  exitWhitelabelEnvironment: () => Promise<void>
}

export type CompanyContextType = CompanyContextValue

const CompanyContext = createContext<CompanyContextValue | null>(null)

const STORAGE_KEY = 'seven_analytics_active_company'
const IMPERSONATION_KEY = 'seven_analytics_impersonated_whitelabel'
const ORIGINAL_TOKEN_KEY = 'seven_analytics_superadmin_original_token'

export function CompanyProvider({ children }: { children: ReactNode }) {
  const [companies, setCompanies] = useState<Company[]>([])
  const [activeCompany, setActiveCompany] = useState<Company | null>(null)
  const [loadingCompanies, setLoadingCompanies] = useState(true)
  const [impersonatedWhitelabel, setImpersonatedWhitelabel] = useState<ImpersonatedWhitelabel | null>(() => {
    try {
      const saved = sessionStorage.getItem(IMPERSONATION_KEY)
      return saved ? JSON.parse(saved) : null
    } catch {
      return null
    }
  })

  const refreshCompanies = useCallback(async () => {
    setLoadingCompanies(true)
    try {
      const list = await companyService.list()
      setCompanies(list)

      // Restore saved company or default to first
      const savedId = localStorage.getItem(STORAGE_KEY)
      const saved = list.find((c) => c.id === savedId) ?? list[0] ?? null
      setActiveCompany(saved)
      if (saved) localStorage.setItem(STORAGE_KEY, saved.id)
    } catch (err) {
      console.error('[CompanyContext] Failed to load companies:', err)
      const msg = (err as Error).message ?? ''
      if (msg.includes('401') || msg.includes('Unauthorized') || msg.includes('Invalid or expired')) {
        // Token inválido/expirado — limpa sessão e redireciona para login
        tokenStorage.remove()
        window.location.href = '/login'
        return
      }
      setCompanies([])
      setActiveCompany(null)
    } finally {
      setLoadingCompanies(false)
    }
  }, [])

  useEffect(() => {
    void refreshCompanies()
  }, [refreshCompanies])

  useEffect(() => {
    if (impersonatedWhitelabel?.branding?.primary_color) {
      if (typeof document !== 'undefined') {
        document.documentElement.style.setProperty('--primary', impersonatedWhitelabel.branding.primary_color)
        if (impersonatedWhitelabel.branding.primary_hover) {
          document.documentElement.style.setProperty('--primary-hover', impersonatedWhitelabel.branding.primary_hover)
        }
        if (impersonatedWhitelabel.branding.primary_light) {
          document.documentElement.style.setProperty('--primary-light', impersonatedWhitelabel.branding.primary_light)
        }
      }
      return
    }

    if (!activeCompany?.id) {
      removeBrandingCssVariables()
      return
    }

    const targetId = activeCompany.id
    const parentId = activeCompany.parent_whitelabel_id

    // Try cache first for instant hydration without FOUC
    try {
      const cached = sessionStorage.getItem(`branding_${targetId}`) ||
        (parentId ? sessionStorage.getItem(`branding_${parentId}`) : null)
      if (cached) {
        const parsed = JSON.parse(cached)
        applyBrandingCssVariables(parsed)
      }
    } catch {
      // Ignore
    }

    // Fetch and sync branding in background
    void brandingService.get(targetId).then((data) => {
      if (data) {
        applyBrandingCssVariables(data)
        try {
          sessionStorage.setItem(`branding_${targetId}`, JSON.stringify(data))
          if (parentId) {
            sessionStorage.setItem(`branding_${parentId}`, JSON.stringify(data))
          }
        } catch {
          // Ignore
        }
      }
    }).catch(() => {
      // Ignore network errors in background sync
    })
  }, [activeCompany?.id, activeCompany?.parent_whitelabel_id, impersonatedWhitelabel])

  async function switchCompany(company: Company) {
    setLoadingCompanies(true)
    try {
      // Validate access on the backend (multi-tenant check)
      await companyService.selectCompany(company.id)

      // Clear local storage and session storage caches for the previous tenant
      const keysToClear: string[] = []
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && (key.includes('filter') || key.includes('search') || key.includes('query') || key.includes('active_tab') || key.includes('sort') || key.includes('page'))) {
          keysToClear.push(key)
        }
      }
      keysToClear.forEach(key => localStorage.removeItem(key))

      const sessionKeysToClear: string[] = []
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i)
        if (key && (key.startsWith('branding_') || key.includes('branding') || key.includes('filter') || key.includes('search') || key.includes('query') || key.includes('active_tab') || key.includes('sort') || key.includes('page'))) {
          sessionKeysToClear.push(key)
        }
      }
      sessionKeysToClear.forEach(key => sessionStorage.removeItem(key))

      if (typeof document !== 'undefined' && !impersonatedWhitelabel) {
        removeBrandingCssVariables()
      }


      // Update the active company state
      setActiveCompany(company)
      localStorage.setItem(STORAGE_KEY, company.id)
    } catch (err) {
      console.error('[CompanyContext] Failed to select company:', err)
      const message = (err as Error).message || 'Acesso negado a esta empresa'
      window.dispatchEvent(new CustomEvent('app:toast', {
        detail: { message, type: 'error' }
      }))
    } finally {
      setLoadingCompanies(false)
    }
  }

  const enterWhitelabelEnvironment = useCallback(async (
    whitelabelId: string,
    whitelabelName: string,
    branding?: { primary_color?: string; primary_hover?: string; primary_light?: string; brand_name?: string | null },
    scopedToken?: string
  ) => {
    const imp: ImpersonatedWhitelabel = {
      id: whitelabelId,
      name: whitelabelName,
      branding,
    }

    if (scopedToken) {
      const currentToken = tokenStorage.get()
      if (currentToken && !sessionStorage.getItem(ORIGINAL_TOKEN_KEY)) {
        sessionStorage.setItem(ORIGINAL_TOKEN_KEY, currentToken)
      }
      tokenStorage.set(scopedToken)
    }

    sessionStorage.setItem(IMPERSONATION_KEY, JSON.stringify(imp))
    setImpersonatedWhitelabel(imp)

    if (branding?.primary_color && typeof document !== 'undefined') {
      document.documentElement.style.setProperty('--primary', branding.primary_color)
      if (branding.primary_hover) {
        document.documentElement.style.setProperty('--primary-hover', branding.primary_hover)
      }
      if (branding.primary_light) {
        document.documentElement.style.setProperty('--primary-light', branding.primary_light)
      }
    }

    // Clear caches for new environment
    const sessionKeysToClear: string[] = []
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i)
      if (key && (key.startsWith('branding_') || key.includes('filter') || key.includes('search') || key.includes('page'))) {
        sessionKeysToClear.push(key)
      }
    }
    sessionKeysToClear.forEach(key => sessionStorage.removeItem(key))

    localStorage.setItem(STORAGE_KEY, whitelabelId)
    await refreshCompanies()
  }, [refreshCompanies])

  const exitWhitelabelEnvironment = useCallback(async () => {
    const originalToken = sessionStorage.getItem(ORIGINAL_TOKEN_KEY)
    if (originalToken) {
      tokenStorage.set(originalToken)
      sessionStorage.removeItem(ORIGINAL_TOKEN_KEY)
    }

    sessionStorage.removeItem(IMPERSONATION_KEY)
    setImpersonatedWhitelabel(null)
    localStorage.removeItem(STORAGE_KEY)

    if (typeof document !== 'undefined') {
      removeBrandingCssVariables()
    }


    // Clear tenant caches
    const sessionKeysToClear: string[] = []
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i)
      if (key && (key.startsWith('branding_') || key.includes('filter') || key.includes('search') || key.includes('page'))) {
        sessionKeysToClear.push(key)
      }
    }
    sessionKeysToClear.forEach(key => sessionStorage.removeItem(key))

    await refreshCompanies()
  }, [refreshCompanies])

  async function switchToPersonalAccount(): Promise<Company | null> {
    const list = companies.length > 0 ? companies : await companyService.list()
    setCompanies(list)
    const personal = list.find((c) => c.account_type === 'personal')
    if (personal) {
      await switchCompany(personal)
      return personal
    }
    await refreshCompanies()
    return null
  }

  async function switchToBusinessAccount(): Promise<Company | null> {
    const list = companies.length > 0 ? companies : await companyService.list()
    setCompanies(list)
    const business = list.find((c) => c.account_type !== 'personal') ?? list[0] ?? null
    if (business) {
      await switchCompany(business)
      return business
    }
    return null
  }

    const isGlobalMaster = Boolean(
      (activeCompany?.is_master === true ||
       companies.some(c => c.is_master === true && (c.user_role === 'owner' || c.user_role === 'admin'))) &&
      (activeCompany?.user_role === 'owner' || activeCompany?.user_role === 'admin' || companies.some(c => c.is_master === true && (c.user_role === 'owner' || c.user_role === 'admin')))
    )

    const isTenantMaster = Boolean(
      (activeCompany?.is_master === true ||
       activeCompany?.is_whitelabel_master === true ||
       activeCompany?.is_whitelabel === true ||
       companies.some(c => (c.is_master === true || c.is_whitelabel_master === true || c.is_whitelabel === true) && (c.user_role === 'owner' || c.user_role === 'admin'))) &&
      (activeCompany?.user_role === 'owner' || activeCompany?.user_role === 'admin' || companies.some(c => (c.is_master === true || c.is_whitelabel_master === true || c.is_whitelabel === true) && (c.user_role === 'owner' || c.user_role === 'admin')))
    )

    const isTitularTenant = Boolean(
      (activeCompany?.is_whitelabel || activeCompany?.is_whitelabel_master || activeCompany?.is_master) &&
      !activeCompany?.parent_whitelabel_id
    )

    const isChildCompany = Boolean(activeCompany?.parent_whitelabel_id)

    return (
      <CompanyContext.Provider
        value={{
          companies,
          activeCompany,
          loadingCompanies,
          isGlobalMaster,
          isTenantMaster,
          isMasterCompany: isTenantMaster,
          isPersonalAccount: activeCompany?.account_type === 'personal',
          isOwnerAccount: companies.some(c => c.user_role === 'owner'),
          isTitularTenant,
          isChildCompany,
          userRole: activeCompany?.user_role ?? null,
          impersonatedWhitelabel,
          switchCompany,
          switchToPersonalAccount,
          switchToBusinessAccount,
          refreshCompanies,
          enterWhitelabelEnvironment,
          exitWhitelabelEnvironment,
        }}
      >
        {children}
      </CompanyContext.Provider>
    )
}

export function useCompanyContext() {
  const ctx = useContext(CompanyContext)
  if (!ctx) throw new Error('useCompanyContext must be used inside CompanyProvider')
  return ctx
}
